/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the canvas.

   MASTERS ARE CONTENT (contract §3 R2.4, locked with Josh 2026-08-23).
   So this is a clean structured sheet with Word-feel typing: no
   header/footer bands, no pre/post body, no page chrome, no
   exact-spacing simulation. "There isn't a format applied until it
   hits a project."

   What IS styled is the LEVEL — because formas map onto levels. The
   presentation is ONE app-standard rendering derived from the shared
   body-layout brain, fed the published CSI SectionFormat ladder
   (shared/csi-defaults.ts) instead of a real forma: PART 1 - / 1.01 /
   A. / 1. / a. / 1) / a) / (1) / (a), each with its indent, hanging
   indent and vertical rhythm. The same brain numbers the .docx, so a
   number on this sheet is the number that prints.

   The caret, selection, undo history, IME, clipboard, tables and
   HTML/TSV paste all belong to ProseMirror. Nothing here is
   hand-rolled: every extension is the Designer's.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import {
  COLLAB_FIELD,
  collabExtensions,
  withoutHistory,
  type SpecCanvasCollab,
} from './collabBinding';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import { csiLevelStyles } from '../../shared/csi-defaults';
import { DESIGNER_NODES, blocksToDoc, docToBlocks } from '../designer/schema';
import { Numbering } from '../designer/numbering';
import { SpecKeymap, UniqueBlockIds } from '../designer/commands';
import { DesignerSearch, openFind } from '../designer/search';
import { SmartBlockView, SpecImageView } from '../designer/nodeviews';
import { TableGrips } from '../designer/TableGrips';
import { ImageToolbar } from './ImageToolbar';
import { selectCellAtCoords } from '../designer/tableCommands';
import { getUiScale, subscribeUiScale } from '../lib/uiScale';
import {
  addBlockBelow,
  BlockClipboard,
  focusBlockAt,
  GROUP_MODULE_REFUSAL,
  groupSelectedBlocks,
  moveSelectedBlocks,
  toggleContinuation,
  ungroupSelectedBlocks,
} from './commands';
import {
  SpecBlockMenu,
  type SpecBlockMenuState,
  type SpecVariantMenuProps,
  type SpecModuleMenuProps,
} from './SpecBlockMenu';
import { VariantProvenance, type VariantPluginOptions } from './variantPlugin';
import { BlockGutter } from './BlockGutter';
import { probeClipboardHasContent } from '../designer/clipboardProbe';
import {
  BlockSelect,
  blockSelection,
  clearBlockSelection,
  blockIdsInTextSelection,
} from './blockSelect';
import { ModuleStamps, stampOfNode } from '../designer/moduleInsert';
import {
  ModuleBuildFlags,
  ModuleDefaultPart,
  ModulePartHeaders,
  ModulePasteStamp,
  repaintModuleFlags,
} from './moduleBuild';
import type { ModuleStamp, ModuleViolation } from '../../shared/spec-modules';

/** The app-standard level presentation this file's header promises: no forma,
 *  so the published CSI SectionFormat ladder (indent, hanging indent, PART
 *  bold+caps, vertical rhythm) is what the numbering plugin resolves to. Built
 *  once — `csiLevelStyles()` is pure and this never changes under the editor —
 *  and StandardConfig-shaped only in the fields `resolveLevelLayout`/
 *  `getCsiLevelFormats` (shared/body-layout.ts) actually read: `levels`. The
 *  numbering SCHEME itself (PART %1 -, %1.%2, A., …) already defaults inside
 *  `getCsiLevelFormats` with no `special.numberFormats`, so nothing here
 *  touches export or the shared brain — only what this sheet decorates with. */
const CSI_DEFAULT_FORMA: StandardConfig = {
  id: 'csi-default',
  docSettings: {} as StandardConfig['docSettings'],
  levels: csiLevelStyles(),
  headerFooter: {} as StandardConfig['headerFooter'],
  special: {} as StandardConfig['special'],
};

/** The master-authoring shortcuts the Designer's keymap does not carry.
 *  Everything else (Tab/Shift+Tab, Ctrl+S/F/D/Z/Y, B/I/U, Esc, Enter,
 *  Backspace) comes from SpecKeymap and ProseMirror itself. */
interface StudioKeymapOptions {
  /** Say something the user needs to hear — used by Ctrl+G when a group would
   *  cut a module insertion in half. The palette and the menu say the same
   *  thing in their disabled tooltips; the keyboard has no tooltip, so it
   *  gets the toast. */
  onNotice?: (msg: string) => void;
}

const StudioKeymap = Extension.create<StudioKeymapOptions>({
  name: 'specStudioKeymap',
  addOptions() {
    return { onNotice: undefined };
  },
  addKeyboardShortcuts() {
    const notice = (msg: string) => this.options.onNotice?.(msg);
    return {
      // Ctrl+H — find AND replace. One bar carries both rows, so it is the
      // same surface Ctrl+F opens, focused on the query box.
      'Mod-h': ({ editor }) => openFind(editor.state, editor.view.dispatch.bind(editor.view)),
      // Word's "insert a paragraph below without splitting this one".
      'Mod-Enter': ({ editor }) =>
        addBlockBelow(editor.state, editor.view.dispatch.bind(editor.view)),
      // Word's own move-paragraph chord. Ctrl+Arrow is deliberately NOT used:
      // in Word that walks the caret by paragraph, and fighting a spec
      // writer's muscle memory is worse than having no shortcut at all.
      'Alt-Shift-ArrowUp': ({ editor }) =>
        moveSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view), -1),
      'Alt-Shift-ArrowDown': ({ editor }) =>
        moveSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view), 1),
      // Continuation is a spec-authoring idea with no Word equivalent, so it
      // gets a mnemonic of its own alongside the menu item.
      'Mod-Shift-c': ({ editor }) =>
        toggleContinuation(editor.state, editor.view.dispatch.bind(editor.view)),
      // Group / Ungroup the handle-selected blocks. Ctrl+G is unbound
      // everywhere else in this editor, and both also live on the Blocks
      // toolbox and in the right-click menu — nothing here is keyboard-only.
      'Mod-g': ({ editor }) => {
        const refusal = groupSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view));
        if (refusal === 'module') notice(GROUP_MODULE_REFUSAL);
        // 'few' is not an error: with nothing (or one block) picked the chord
        // simply does not apply, and the key falls through untouched.
        return refusal === null;
      },
      'Mod-Shift-g': ({ editor }) =>
        ungroupSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view)),
    };
  },
});

export interface SpecStudioCanvasProps {
  /** Identity of the loaded document. Changing it rebuilds the editor, so
   *  two masters never share an undo history. */
  docKey: string;
  blocks: ContentBlock[];
  specNumber: string;
  specName: string;
  zoom: number;
  editable: boolean;
  canDelete: boolean;
  /** The document, both ways: the blocks a save writes, and the raw node JSON
   *  the variant derive reads (it needs the `meta` a ContentBlock deliberately
   *  drops — see variantOps). */
  onChange: (blocks: ContentBlock[], doc: JSONContent) => void;
  onSave: () => void;
  /** A one-line message for the user — the host's toast. Absent ⇒ the
   *  keyboard's refusals stay silent, which is why every one of them also has
   *  a visible, disabled-with-a-reason control. */
  onNotice?: (msg: string) => void;
  onEditorReady?: (editor: Editor | null) => void;
  /** Ctrl+wheel zoom (Cohesion R1 Phase 2 item 2 — ported verbatim from
   *  forma-studio/StudioCanvas.tsx's wheelZoomRef). Absent ⇒ the surface
   *  offers no zoom control to drive, so nothing is wired. */
  onZoomDelta?: (step: number) => void;
  /** Present only in a variant (Masters Ladder R3): provenance paint plus
   *  the guard that keeps an edit expressible as a layer op. */
  variant?: VariantPluginOptions | null;
  /** Extra items for the block menu — the variant's hide / restore / revert. */
  variantMenu?: SpecVariantMenuProps | null;
  /** "Save as module…" in the right-click menu (Phase 4). Acts on the whole
   *  gutter selection, not just the block that was clicked — the Studio reads
   *  `selectedBlocks` itself when the item fires. */
  moduleMenu?: SpecModuleMenuProps | null;
  /** MODULE MODE (R2 Phase 4). Present ⇒ this canvas is editing a module
   *  DEFINITION, not a spec: level-2 articles carry the part control and the
   *  structure law's violations paint as inline flags. Absent ⇒ nothing about
   *  the canvas changes, which is why the Studio needs no branch. */
  moduleBuild?: ModuleBuildOptions | null;
  /** LIVE SHARED DRAFT (docs/SPEC_WORKFLOW.md §5). Present ⇒ the document
   *  IS the shared Yjs fragment: `blocks` is ignored (the fragment is the
   *  content), ProseMirror's History is swapped for the Yjs undo manager
   *  (Ctrl+Z undoes only YOUR edits), and everyone else's caret shows with
   *  their name. Part of the editor's identity, like `docKey`. */
  collab?: SpecCanvasCollab | null;
}

// The binding helpers live in collabBinding.ts (shared with the Designer
// canvas); these re-exports keep every existing importer working.
export { COLLAB_FIELD };
export type { SpecCanvasCollab };

/** What module mode needs from its host. The violations are DERIVED by the
 *  builder from the same block list it holds — the canvas only paints them. */
export interface ModuleBuildOptions {
  /** Block id → the one flag shown on it (`moduleViolationsByBlock`). */
  violations: Record<string, ModuleViolation>;
}

export function SpecStudioCanvas({
  docKey,
  blocks,
  specNumber,
  specName,
  zoom,
  editable,
  canDelete,
  onChange,
  onSave,
  onNotice,
  onEditorReady,
  onZoomDelta,
  variant,
  variantMenu,
  moduleMenu,
  moduleBuild,
  collab,
}: SpecStudioCanvasProps) {
  // UI Scale dial (DESIGN.md §3.6): `zoom` is the RELATIVE percent (1.0 =
  // matches surroundings); the applied transform is relative × dial so the
  // page stays print-faithful next to the surrounding chrome at every
  // window width.
  const [dial, setDial] = React.useState(() => getUiScale().dial);
  React.useEffect(() => subscribeUiScale((v) => setDial(v.dial)), []);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;
  const onNoticeRef = React.useRef(onNotice);
  onNoticeRef.current = onNotice;
  // Module mode is part of the document's identity (`docKey` changes with the
  // module), so this is read through a ref for the same reason the variant is:
  // the plugin sees the live violations without the editor being rebuilt.
  const moduleBuildRef = React.useRef(moduleBuild);
  moduleBuildRef.current = moduleBuild;
  // Read through a ref so the plugin always sees the live variant without the
  // editor being rebuilt on every derive.
  const variantRef = React.useRef(variant);
  variantRef.current = variant;

  const editor = useEditor(
    {
      extensions: [
        // A shared document's undo is the Yjs undo manager (Collaboration
        // below) — ProseMirror's History would undo OTHER people's edits.
        ...(collab ? withoutHistory(DESIGNER_NODES) : DESIGNER_NODES),
        ...(collab ? collabExtensions(collab) : []),
        DesignerSearch,
        UniqueBlockIds,
        // No forma: masters render in the app-standard level presentation —
        // the CSI ladder built above, not the brain's own (indent-less) null
        // fallback, which exists for callers that truly have nothing to show.
        // `moduleMode` is static for this editor's life (module mode never
        // toggles under a live canvas), so it is read straight off the prop.
        Numbering.configure({ getForma: () => CSI_DEFAULT_FORMA, moduleMode: !!moduleBuild }),
        SpecKeymap.configure({ onSave: () => onSaveRef.current() }),
        StudioKeymap.configure({ onNotice: (msg) => onNoticeRef.current?.(msg) }),
        // The discrete BLOCK selection the gutter grips drive (Phase 1 item
        // 4). It holds only ids, paints them, and is what `selectedBlocks`
        // resolves to — the text selection, caret and typing are untouched.
        BlockSelect,
        // Delete / Ctrl+C / Ctrl+X / Ctrl+V on a handle-selected run of blocks
        // (Table Standard Tranche 4). Listed AFTER BlockSelect because it
        // reads that selection, and after the keymaps because a later
        // extension's shortcuts are consulted first — Delete and Backspace
        // have to reach it before ProseMirror's own merge/join, and they only
        // ever answer when the gutter actually holds blocks.
        BlockClipboard,
        // Blocks that arrived by inserting a module wear a quiet rail, so an
        // insertion reads as one thing (Phase 5). The stamp itself lives in
        // the document, not in this plugin — the paint is derived from it.
        ModuleStamps,
        // MODULE MODE: the structure law's inline flags (R2 Ruling 6). Off
        // entirely when this canvas is editing a spec.
        ...(moduleBuildRef.current
          ? [
              ModuleBuildFlags.configure({
                getViolations: () => moduleBuildRef.current?.violations ?? {},
              }),
              // The three PART group headers (Phase 1 item 1) and the
              // default-part auto-fill for a freshly-added article
              // (item 4) — both read the document itself, so neither needs
              // the ref trick the violations above do.
              ModulePartHeaders,
              ModuleDefaultPart,
              // Cut/copy/paste into a different part (Module Zones Phase 3):
              // stamps the pasted content's articles with the DESTINATION
              // part, overriding whatever the source carried, and strips any
              // `excluded` a cross-window paste might drag in.
              ModulePasteStamp,
            ]
          : []),
        // A variant is part of the document's identity (`docKey`), so this is
        // configured once per variant and never toggled under a live editor.
        ...(variantRef.current
          ? [
              VariantProvenance.configure({
                getView: () => variantRef.current?.getView() ?? null,
                onTag: (kind, blockId, rect) => variantRef.current?.onTag?.(kind, blockId, rect),
                onGate: (reason) => variantRef.current?.onGate?.(reason),
                getPending: () => variantRef.current?.getPending?.() ?? new Set<string>(),
              }),
            ]
          : []),
      ],
      // Shared: the fragment is the content (Collaboration renders it).
      content: collab ? undefined : blocksToDoc(blocks),
      editable,
      editorProps: {
        attributes: {
          class: 'dsg-doc sps-doc',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': `${specNumber} ${specName} — specification text`,
        },
        nodeViews: {
          specImage: (node) => new SpecImageView(node),
          smartBlock: (node) => new SmartBlockView(node),
        },
      },
      onUpdate: ({ editor: ed, transaction }) => {
        if (!transaction.docChanged) return;
        const json = ed.getJSON();
        onChangeRef.current(docToBlocks(json), json);
      },
    },
    [docKey, collab?.ydoc]
  );

  React.useEffect(() => {
    onEditorReady?.(editor ?? null);
    return () => onEditorReady?.(null);
  }, [editor, onEditorReady]);

  // A violation appears and disappears without the document changing (a part
  // is assigned, a flag clears), so the paint is told to re-read them.
  const violations = moduleBuild?.violations;
  React.useEffect(() => {
    if (!editor || !violations) return;
    repaintModuleFlags(editor.state, editor.view.dispatch.bind(editor.view));
  }, [editor, violations]);

  // A variant switch rebuilds the editor (`docKey` above), which mounts with no
  // DOM focus at all — the caret is technically at the doc's start in
  // ProseMirror state, but nothing puts the browser's focus on the sheet, so
  // it never PAINTS. The first render after a real master is open is not a
  // switch (nobody asked to be in the document yet), so it is skipped.
  const openedOnce = React.useRef(false);
  React.useEffect(() => {
    if (!editor) return;
    if (!openedOnce.current) {
      openedOnce.current = true;
      return;
    }
    editor.commands.focus();
  }, [editor]);

  // `editable` only ever moves under real permission changes today, but the
  // caret's other invisibility mode is exactly this shape — a read-only pass
  // borrows the DOM focus and nothing gives it back — so the restore is
  // unconditional insurance, not dead code for the case that exists now.
  const wasEditableRef = React.useRef(editable);
  const hadFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (!editor) return;
    const turningOff = wasEditableRef.current && !editable;
    const turningOn = !wasEditableRef.current && editable;
    if (turningOff) hadFocusRef.current = editor.isFocused;
    editor.setEditable(editable);
    if (turningOn && hadFocusRef.current) editor.commands.focus();
    wasEditableRef.current = editable;
  }, [editor, editable]);

  // Native non-passive listener — React's synthetic onWheel is passive, so
  // preventDefault can't stop the browser's own page-zoom there (ported
  // verbatim from forma-studio/StudioCanvas.tsx's wheelZoomRef).
  const wheelZoomRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !onZoomDelta) return;
      el.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          if (!e.ctrlKey) return;
          e.preventDefault();
          onZoomDelta(e.deltaY < 0 ? 0.05 : -0.05);
        },
        { passive: false }
      );
    },
    [onZoomDelta]
  );

  // ─── Right-click a block ───
  const [menu, setMenu] = React.useState<SpecBlockMenuState | null>(null);
  // The clipboard is probed first (80ms cap) so Paste never flashes.
  const openMenu = (s: SpecBlockMenuState) => {
    void probeClipboardHasContent().then((has) => setMenu({ ...s, clipboardHas: has }));
  };
  const onContextMenu = (e: React.MouseEvent) => {
    if (!editor) return;
    const el = (e.target as HTMLElement).closest('[data-block-id]');
    if (!el) return;
    e.preventDefault();
    const id = el.getAttribute('data-block-id');
    let kind: SpecBlockMenuState['kind'] = 'text';
    let pos = -1;
    let size = 0;
    let moduleStamp: ModuleStamp | null = null;
    editor.state.doc.forEach((node, offset) => {
      if (node.attrs.blockId !== id) return;
      pos = offset;
      size = node.nodeSize;
      // Read from the NODE, not from a class: the stamp is data, and the menu
      // must never offer to remove an insertion the document does not have.
      moduleStamp = stampOfNode(node);
      kind =
        node.type.name === 'table'
          ? 'table'
          : node.type.name === 'specImage'
            ? 'image'
            : node.type.name === 'smartBlock'
              ? 'smart'
              : 'text';
    });
    // Right-clicking a block the caret is not in must act on THAT block —
    // move the selection there first, exactly as Word does. A selection that
    // already covers the block (a multi-block drag) is left alone.
    //
    // Inside a table the same rule goes one level finer: the menu's table
    // commands act on the CELL under the pointer, not on the table's first
    // cell, so the selection lands there instead.
    //
    // A GUTTER selection outranks both: right-clicking a block that is part
    // of it acts on the whole set (`selectedBlocks` already resolves to it),
    // so nothing is moved and nothing is narrowed. Right-clicking OUTSIDE it
    // drops it first — Word's rule, one level up.
    const picked = blockSelection(editor.state).ids;
    const inPicked = !!id && picked.includes(id);
    if (picked.length && !inPicked) {
      clearBlockSelection(editor.state, editor.view.dispatch.bind(editor.view));
    }
    const inCell = !!(e.target as HTMLElement).closest('td,th');
    const { from, to } = editor.state.selection;
    const inside = pos >= 0 && from >= pos && to <= pos + size;
    // A highlighted TEXT run spanning several blocks is a selection too
    // (Explorer rule, Josh 2026-09-22): right-clicking a block inside it
    // keeps the run, so the menu acts on every block highlighted.
    const textRun = blockIdsInTextSelection(editor.state);
    const inTextRun = !!id && textRun.length > 1 && textRun.includes(id);
    if ((inPicked && picked.length > 1) || inTextRun) {
      // Leave the caret and the cell selection alone: the menu is about the
      // whole run, not about where inside it the pointer landed.
    } else if (inCell) {
      selectCellAtCoords(editor, e.clientX, e.clientY);
    } else if (pos >= 0 && !inside) {
      focusBlockAt(editor.state, editor.view.dispatch.bind(editor.view), pos);
    }
    openMenu({
      x: e.clientX,
      y: e.clientY,
      level: Number(el.getAttribute('data-level')) || 1,
      excluded: el.classList.contains('dsg-excluded'),
      continuation: el.classList.contains('dsg-continuation'),
      kind,
      blockId: id || '',
      // The variant paints these classes, so the menu reads the same truth the
      // eye does — no second provenance lookup that could disagree.
      origin: el.classList.contains('sps-prov-layer') ? 'layer' : 'core',
      overridden: el.classList.contains('sps-prov-override'),
      ghost: el.classList.contains('sps-ghost'),
      pendingUpdate: el.classList.contains('sps-prov-update'),
      moduleStamp,
    });
  };

  return (
    <div className="dsg-canvas sps-canvas" ref={wheelZoomRef}>
      <div
        className="sps-sheet"
        onContextMenu={onContextMenu}
        style={{ transform: `scale(${zoom * dial})`, transformOrigin: 'top center' }}
      >
        <div className="sps-sheet-id">
          {specNumber && <span className="sps-sheet-num">Section {specNumber}</span>}
          <span className="sps-sheet-name">{specName}</span>
        </div>
        <EditorContent editor={editor} />
      </div>

      {menu && editor && (
        <SpecBlockMenu
          // Radix's Popper only recomputes the menu's screen position on the
          // OPEN transition (`open` goes false → true), never on a same-instance
          // prop update. `open` below is hardcoded true, so re-using the same
          // component instance across right-clicks (menu state simply mutating
          // x/y) left every menu after the first stuck at the first click's
          // spot. Keying on the click coordinates forces a fresh mount — and a
          // fresh open transition — every time, so Radix always measures the
          // NEW anchor position.
          key={`${menu.blockId}:${menu.x}:${menu.y}`}
          editor={editor}
          state={menu}
          canEdit={editable}
          canDelete={canDelete}
          variant={variantMenu ?? null}
          moduleMenu={moduleMenu ?? null}
          moduleMode={!!moduleBuild}
          onClose={() => {
            setMenu(null);
            editor.commands.focus();
          }}
        />
      )}

      {editor && <TableGrips editor={editor} canEdit={editable} />}
      {editor && <ImageToolbar editor={editor} canEdit={editable} />}
      {editor && (
        <BlockGutter
          editor={editor}
          canEdit={editable}
          moduleMode={!!moduleBuild}
          onNotice={onNotice}
        />
      )}
    </div>
  );
}
